// src/components/LoadingFacts/LoadingFacts.js

import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Plus, Edit, Trash2, X, Search } from 'lucide-react';
import './LoadingFacts.css';

const LOADING_FACTS = process.env.REACT_APP_API_URL;
const API_URL =    `${LOADING_FACTS}/api/loading-facts`;     // Your backend API endpoint

const LoadingFacts = () => {
  const [facts, setFacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState(''); // State for the search filter
  const [newFactText, setNewFactText] = useState('');
  const [editingFact, setEditingFact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch all facts from the server
  const fetchFacts = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(API_URL);
      setFacts(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch facts. Please try again later.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFacts();
  }, []);

  // Filter facts based on search query. useMemo prevents re-filtering on every render.
  const filteredFacts = useMemo(() => {
    if (!searchQuery) {
      return facts;
    }
    return facts.filter(fact =>
      fact.text.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [facts, searchQuery]);

  // Handler for adding a new fact
  const handleAddFact = async (e) => {
    e.preventDefault();
    if (!newFactText.trim()) return;

    try {
      const response = await axios.post(API_URL, { text: newFactText });
      setFacts([response.data, ...facts]);
      setNewFactText('');
      setError('');
    } catch (err) {
      setError('Failed to add fact. It might already exist.');
      console.error(err);
    }
  };

  // Handler for deleting a fact
  const handleDeleteFact = async (id) => {
    if (window.confirm('Are you sure you want to delete this fact?')) {
      try {
        await axios.delete(`${API_URL}/${id}`);
        setFacts(facts.filter((fact) => fact._id !== id));
      } catch (err) {
        setError('Failed to delete fact.');
        console.error(err);
      }
    }
  };

  // Handler for updating a fact
  const handleUpdateFact = async (e) => {
    e.preventDefault();
    if (!editingFact || !editingFact.text.trim()) return;

    try {
      const response = await axios.put(`${API_URL}/${editingFact._id}`, { text: editingFact.text });
      setFacts(facts.map((fact) => (fact._id === editingFact._id ? response.data : fact)));
      setEditingFact(null);
    } catch (err) {
      setError('Failed to update fact.');
      console.error(err);
    }
  };

  return (
    <div className="loading-facts-container">
         <Helmet>
        <title>Loading Facts - Admin Panel</title>
      </Helmet>
      <div className="facts-header">
        <h1>Manage Loading Facts</h1>
        <p>Add, edit, or remove facts shown to users during loading screens.</p>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* --- SEARCH FILTER INPUT --- */}
      <div className="search-facts-wrapper">
        <Search className="search-icon" size={20} />
        <input
          type="text"
          className="search-facts-input"
          placeholder="Search through facts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>


      <div className="add-fact-form">
        <form onSubmit={handleAddFact}>
          <input
            type="text"
            value={newFactText}
            onChange={(e) => setNewFactText(e.target.value)}
            placeholder="Enter a new interesting fact..."
          />
          <button type="submit" className="add-btn">
            <Plus size={18} /> Add Fact
          </button>
        </form>
      </div>

      <div className="facts-list">
        {isLoading ? (
          <p>Loading facts...</p>
        ) : (
          filteredFacts.map((fact) => (
            <div className="fact-card" key={fact._id}>
              <p className="fact-text">{fact.text}</p>
              <div className="fact-actions">
                <button onClick={() => setEditingFact({ ...fact })} className="edit-btn">
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDeleteFact(fact._id)} className="delete-btn">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
        )}
        {!isLoading && filteredFacts.length === 0 && (
          <p className="no-results-message">
            {facts.length > 0 ? "No facts match your search." : "No facts found. Add one to get started!"}
          </p>
        )}
      </div>

      {/* Edit Modal */}
      {editingFact && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Edit Fact</h2>
            <form onSubmit={handleUpdateFact}>
              <textarea
                value={editingFact.text}
                onChange={(e) => setEditingFact({ ...editingFact, text: e.target.value })}
                rows="4"
              />
              <div className="modal-actions">
                <button type="button" onClick={() => setEditingFact(null)} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  Save Changes
                </button>
              </div>
            </form>
            <button className="modal-close-btn" onClick={() => setEditingFact(null)}>
                <X size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoadingFacts;